import { QueryClientContext } from '@tanstack/react-query';
import { useCallback, useContext, useMemo, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useSiteConfig } from 'src/contexts/SiteGuardContext';
import { DEFAULT_MARKET, type DistanceUnit, type Market, type TravelerHomeCountry } from 'src/types/market.type';
import { formatDistance, type DistanceStyle } from 'src/utils/distance.util';
import { SUPPORTED_TRAVELER_HOME_COUNTRIES, isTravelerHomeCountry } from 'src/utils/country.util';

/** The react-query key the traveler org pages (OrganizationGuard, checkout, SSR seed) share. */
export const orgDetailQueryKey = (slug: string) => ['getOrganizationDetailBySlug', slug];

/**
 * NPT-2051 — DEFAULT_MARKET <- site market <- org market. The org payload's `market` is already
 * resolved over the site by the API, so when it is present it wins outright; each layer is merged
 * over the defaults so a partial or older payload can never leave a field undefined.
 */
export const resolveMarket = (siteMarket?: Partial<Market> | null, orgMarket?: Partial<Market> | null): Market => {
   const merged: Market = { ...DEFAULT_MARKET, ...(siteMarket ?? {}), ...(orgMarket ?? {}), currency: 'USD' };
   if (!merged.searchBiasCountries?.length) merged.searchBiasCountries = DEFAULT_MARKET.searchBiasCountries;
   return merged;
};

const noopUnsubscribe = () => {};

type OrgDetailState = { data?: { data?: { market?: Market } }; status: string } | undefined;

/**
 * Reads the org detail the traveler pages already fetched (`['getOrganizationDetailBySlug', slug]`)
 * without starting a second request, and without requiring a QueryClientProvider (shared
 * components render outside one in some tests). `pending`: that request is still on its way,
 * so the org's market is not known yet (an entry nobody created is not pending: nothing is
 * coming, and the site market is the answer).
 */
const useCachedOrgMarket = (slug?: string): { market?: Market; pending: boolean } => {
   const client = useContext(QueryClientContext);
   const subscribe = useCallback(
      (onChange: () => void) => (client && slug ? client.getQueryCache().subscribe(onChange) : noopUnsubscribe),
      [client, slug],
   );
   // The query's state object is replaced on every change and stable otherwise: a valid snapshot.
   const getSnapshot = (): OrgDetailState =>
      client && slug ? (client.getQueryCache().find({ queryKey: orgDetailQueryKey(slug), exact: true })?.state as OrgDetailState) : undefined;
   const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
   return { market: state?.data?.data?.market, pending: state?.status === 'pending' };
};

/**
 * The market the current page sells into: the org's (traveler routes under `/c/:organizationSlug`),
 * else the site's, else DEFAULT_MARKET. `pending` is true on an org route while the org detail is
 * still loading: `market` is then the site's, and may still change to the org's. Requests whose
 * identity depends on the market (residency) should wait for it.
 */
export const useMarketStatus = (): { market: Market; pending: boolean } => {
   const { organizationSlug } = useParams();
   const { siteConfig } = useSiteConfig();
   const { market: orgMarket, pending } = useCachedOrgMarket(organizationSlug);
   const siteMarket = siteConfig?.market;
   const market = useMemo(() => resolveMarket(siteMarket, orgMarket), [siteMarket, orgMarket]);
   return { market, pending };
};

/** See useMarketStatus. */
export const useMarket = (): Market => useMarketStatus().market;

/**
 * NPT-2008 — the residency a hotel search / rates request is made for. `explicit` is a
 * countryCode the request already names (the URL's); otherwise the market's default country.
 * - `defaultResidency`: what to send when the URL names none (renderRequestFilter's fallback).
 * - `residency`: what the request will actually carry, lower case; put it in the query key so a
 *   market that changes it refetches instead of serving the other country's quote.
 * - `pending`: the answer may still change (org market loading, nothing explicit): wait.
 */
export const useRequestResidency = (explicit?: string | null) => {
   const { market, pending } = useMarketStatus();
   const defaultResidency = market.defaultCountry.toLowerCase();
   return {
      defaultResidency,
      residency: (explicit || defaultResidency).toLowerCase(),
      pending: pending && !explicit,
   };
};

/** The countries the platform sells into, as the site payload reports them (fixed allowlist otherwise). */
export const useTravelerHomeCountries = (): readonly TravelerHomeCountry[] => {
   const { siteConfig } = useSiteConfig();
   const fromSite = siteConfig?.travelerHomeCountries;
   return useMemo(() => {
      const valid = (fromSite ?? []).filter(isTravelerHomeCountry);
      return valid.length ? valid : SUPPORTED_TRAVELER_HOME_COUNTRIES;
   }, [fromSite]);
};

/**
 * NPT-2055 — formats a distance held in miles in the market's unit (or `unit` when the caller
 * knows better, e.g. an admin page showing a specific org's market).
 */
export const useFormatDistance = (unit?: DistanceUnit) => {
   const { distanceUnit } = useMarket();
   const { t } = useTranslation();
   const resolved = unit ?? distanceUnit;
   return useCallback(
      (miles: number, options?: { style?: DistanceStyle; digits?: number | 'auto' }) => formatDistance(miles, resolved, t, options),
      [resolved, t],
   );
};
